import { Link } from "expo-router"
import { Pressable, View } from "react-native"
import { useState, ComponentProps, useMemo } from "react"

import { ar } from "@src/i18n"
import { ModalWrapper, Text, TextInput, Button, Header, List, ActivityIndicator, MCIcon } from "@src/components"
import { sharedStyles, priceFromDBFormat, formatDateForDisplay, trpc, statusColors } from "@src/utils"
import { OrderStatus, OrderSource } from "@white-labels/input"
import { Show, useObservable } from "@legendapp/state/react"

const sourceIcons: Record<OrderSource, ComponentProps<typeof MCIcon>["name"]> = { app: "android", phone: "phone", whatsapp: "whatsapp", manual: "hand-coin", pos: "store" }

export default function Orders() {
  const [filters, setFilters] = useState<{ status?: OrderStatus[]; source?: OrderSource[]; orderNumber?: string }>({})
  const filter$ = useObservable(false)
  const [tempStatus, setTempStatus] = useState<OrderStatus[]>([])
  const [tempSource, setTempSource] = useState<OrderSource[]>([])
  const [tempOrderNumber, setTempOrderNumber] = useState("")

  const orders = trpc.orders.list.useInfiniteQuery(
    { status: filters.status, source: filters.source, orderNumber: filters.orderNumber },
    { getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined },
  )

  const allOrders = useMemo(() => orders.data?.pages.flatMap((page) => page.items) ?? [], [orders.data])

  const hasActiveFilters = !!(filters.status?.length || filters.source?.length || filters.orderNumber)

  const openFilter = () => {
    setTempStatus(filters.status || [])

    setTempSource(filters.source || [])
    setTempOrderNumber(filters.orderNumber || "")
    filter$.set(true)
  }

  const applyFilter = () => {
    setFilters({ status: tempStatus.length > 0 ? tempStatus : undefined, source: tempSource.length > 0 ? tempSource : undefined, orderNumber: tempOrderNumber || undefined })
    filter$.set(false)
  }

  const clearFilter = () => {
    setTempStatus([])
    setTempSource([])
    setTempOrderNumber("")
    setFilters({})
    filter$.set(false)
  }

  const toggleStatus = (status: OrderStatus) => {
    setTempStatus((prev) => (prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]))
  }

  const toggleSource = (source: OrderSource) => {
    setTempSource((prev) => (prev.includes(source) ? prev.filter((s) => s !== source) : [...prev, source]))
  }

  const allStatuses: OrderStatus[] = ["pending", "confirmed", "preparing", "out_for_delivery", "delivered", "cancelled", "rejected", "delivery_failed"]
  const allSources: OrderSource[] = ["app", "phone", "whatsapp"]

  const header = (
    <Header
      title={ar.orders}
      right={
        <View className="flex-row h-full">
          <Link href="/dashboard/orders/new" asChild>
            <Pressable hitSlop={12} className="h-full justify-center px-1">
              <MCIcon name="plus" size={24} color="#3b82f6" />
            </Pressable>
          </Link>
          <Pressable hitSlop={12} onPress={openFilter} className="h-full justify-center px-1">
            <MCIcon name="filter-variant" size={24} color={hasActiveFilters ? "#3b82f6" : "#4b5563"} />
          </Pressable>
          <Pressable hitSlop={12} onPress={() => orders.refetch()} className="h-full justify-center px-1">
            <MCIcon name="refresh" size={24} color={hasActiveFilters ? "#3b82f6" : "#4b5563"} />
          </Pressable>
        </View>
      }
    />
  )

  return (
    <>
      {header}
      <List
        data={allOrders}
        contentContainerStyle={sharedStyles.contentContainer}
        onRefresh={orders.refetch}
        onEndReached={() => {
          if (orders.hasNextPage && !orders.isFetchingNextPage) orders.fetchNextPage()
        }}
        onEndReachedThreshold={0.5}
        keyExtractor={(item) => item.id.toString()}
        renderItem={({ item }) => {
          const statusColor = statusColors[item.status as OrderStatus] || "#6b7280"
          const sourceIcon = (sourceIcons[item.source as OrderSource] || "help-circle") as ComponentProps<typeof MCIcon>["name"]

          return (
            <Link asChild href={`/dashboard/orders/${item.id}`}>
              <Pressable className="p-2 bg-white rounded-lg border border-gray-200 shadow-xs mb-2">
                <View className="flex-row items-center justify-between mb-1">
                  <View className="flex-row items-center gap-1">
                    <Text className="font-bold">#{item.orderNumber}</Text>
                    <MCIcon name={sourceIcon} size={16} color="#6b7280" />
                  </View>
                  <View className="px-2 py-1 rounded" style={{ backgroundColor: `${statusColor}20` }}>
                    <Text className="text-xs font-semibold" style={{ color: statusColor }}>
                      {ar[item.status]}
                    </Text>
                  </View>
                </View>

                <Text className="text-gray-700 mb-1">{item.customer?.name || "Unknown Customer"}</Text>
                <Text className="text-gray-500 text-sm mb-1">{formatDateForDisplay(item.createdAt)}</Text>

                <View className="flex-row items-center justify-between pt-2 border-t border-gray-100">
                  <Text className="text-gray-600 text-sm">{ar.total}</Text>
                  <View className="flex-row items-center gap-1">
                    <Text className="font-bold text-lg text-gray-800">{priceFromDBFormat(item.total)}</Text>
                    <Text className="text-gray-500">{ar.egp}</Text>
                  </View>
                </View>
              </Pressable>
            </Link>
          )
        }}
        ListEmptyComponent={orders.isPending ? <ActivityIndicator className="text-gray-400 p-8" /> : <Text className="text-gray-400 p-8 text-center">{ar.noOrders}</Text>}
        ListFooterComponent={orders.isFetchingNextPage ? <ActivityIndicator size="small" color="#3b82f6" className="py-4 items-center" /> : null}
      />

      <Show if={filter$}>
        <ModalWrapper onClose={() => filter$.set(false)} className="p-2">
          <Text className="text-lg font-bold mb-2 text-center">{ar.filter}</Text>

          {/* Order Number Filter */}
          <Text className="mb-2 text-gray-700">{ar.searchByOrderNumber}</Text>
          <TextInput placeholder={ar.searchByOrderNumber} value={tempOrderNumber} onChangeText={setTempOrderNumber} className="mb-2" />

          {/* Status Filter */}
          <Text className="mb-2 text-gray-700">{ar.filterByStatus}</Text>
          <View className="flex-row flex-wrap gap-2 mb-2">
            {allStatuses.map((status) => {
              const isSelected = tempStatus.includes(status)
              const color = statusColors[status]
              return (
                <Pressable
                  key={status}
                  onPress={() => toggleStatus(status)}
                  className="px-3 py-2 rounded-sm border"
                  style={{
                    backgroundColor: isSelected ? `${color}20` : "#fff",
                    borderColor: isSelected ? color : "#d1d5db",
                  }}
                >
                  <Text style={{ color: isSelected ? color : "#6b7280" }}>{ar[status]}</Text>
                </Pressable>
              )
            })}
          </View>

          {/* Source Filter */}
          <Text className="mb-2 text-gray-700">{ar.filterBySource}</Text>
          <View className="flex-row flex-wrap gap-2 mb-2">
            {allSources.map((source) => {
              const isSelected = tempSource.includes(source)
              return (
                <Pressable
                  key={source}
                  onPress={() => toggleSource(source)}
                  className="px-3 py-2 rounded-sm border flex-row items-center gap-2"
                  style={{
                    backgroundColor: isSelected ? "#3b82f620" : "#fff",
                    borderColor: isSelected ? "#3b82f6" : "#d1d5db",
                  }}
                >
                  <MCIcon name={sourceIcons[source]} size={16} color={isSelected ? "#3b82f6" : "#6b7280"} />
                  <Text style={{ color: isSelected ? "#3b82f6" : "#6b7280" }}>{ar[source]}</Text>
                </Pressable>
              )
            })}
          </View>

          <View className="flex-row gap-2">
            <Button onPress={clearFilter} className="flex-1 bg-[#ef4444]">
              {ar.clearFilters}
            </Button>
            <Button onPress={applyFilter} className="flex-1">
              {ar.apply}
            </Button>
          </View>
        </ModalWrapper>
      </Show>
    </>
  )
}
